import { AppEnvironment } from './environment.model';

/**
 * Test / staging environment. Built with production optimizations but pointed
 * at the test backend and more verbose logging.
 *
 * `auth.redirectUri` uses the CUSTOM domain, never the generated
 * `*.azurestaticapps.net` one. Static Web Apps mints a new generated hostname
 * whenever the resource is recreated, and the moment it differs from what is
 * registered in Identity the sign-in returns to an origin that never started
 * it — where `sessionStorage` is empty, the `state` check fails, and the user
 * is told the attempt "could not be verified". The custom domain is stable, so
 * the registry entry stays valid across redeploys.
 *
 * `api.modules` are the module backends on the DEV stand (App Services saphirweblohn-we,
 * saphirwebfakt-we and saphirwebfleet-we). `auth.idpBaseUrl` is real: the shared DEV Identity serves every
 * non-production environment.
 */
export const environment: AppEnvironment = {
  name: 'test',
  production: true,
  api: {
    // Every host here must also be in the CSP `connect-src`
    // (public/staticwebapp.config.json).
    modules: {
      // The Lohnkern backend on the DEV stand. Reads `X-Company-Id`.
      lohn: {
        baseUrl: 'https://saphirweblohn-we-fkf2gfdbdfgyc0g7.westeurope-01.azurewebsites.net',
        companyHeader: true,
      },
      // The accounting backend on the DEV stand; handed on as `FAKT_API_BASE_URL`.
      fakt: {
        baseUrl: 'https://saphirwebfakt-we-hacecbh0a3d4cwb4.westeurope-01.azurewebsites.net/api/v1',
      },
      // The fleet backend on the DEV stand (App Service saphirwebfleet-we); handed on as
      // `FLEET_API_BASE_URL`. The tenant travels in the route, so no company header.
      fleet: {
        baseUrl: 'https://saphirwebfleet-we-bvefacgygcamcpct.westeurope-01.azurewebsites.net',
      },
      // The Calendar backend on the DEV stand (App Service saphirwebcalendar-we); handed on
      // as `CALENDAR_API_BASE_URL`. The module sets `X-Company-Id` itself, so no company header.
      // Azure host until the custom domain saphirweb-calendar-api-dev.saphir-software.net is bound.
      calendar: {
        baseUrl: 'https://saphirwebcalendar-we-fvd2bsg0e3azazeu.westeurope-01.azurewebsites.net/api',
      },
    },
    timeoutMs: 30_000,
  },
  auth: {
    idpBaseUrl: 'https://saphirweb-identity-api-dev.saphir-software.net',
    clientId: 'saphir-web-shell',
    redirectUri: 'https://saphirweb-dev.saphir-software.net/auth/callback',
    useMockAuth: false,
  },
  umc: {
    baseUrl: 'https://saphirweb-umc-api-dev.saphir-software.net',
    // The DEV catalogue's ModulePublicId values — same UMC as environment.ts.
    // Every commercial module is mapped: DEV always gates on licences, so it
    // shows what a customer would see.
    moduleIds: {
      lohn: '122b93e4-e790-f111-9b33-6045bdf577a9',
      // "Fakt/Fibu" in the DEV catalogue — same GUID the FinancialAccounting
      // backend carries as its `moduleId`.
      fakt: '1d120ab4-dc99-408e-9d11-1b350f6c9fd5',
      calendar: 'ee676c56-4119-4d33-afe4-1b8c2d63ea29',
      // "Fuhrpark" (code `fuhr-park`) in the DEV catalogue.
      fleet: 'f5686f2a-6c4b-4e96-bff4-69acb45b4676',
    },
  },

  // The DEV stand's SPAs — the "-api" hosts above are their backends.
  // `helpUrl` is not set yet: there is no support destination to point at.
  links: {
    umcConsoleUrl: 'https://saphirweb-umc-dev.saphir-software.net',
    identityWebUrl: 'https://saphirweb-identity-dev.saphir-software.net',
  },

  logLevel: 'info',
};
